import { generateClient } from 'aws-amplify/api';
import { type GraphQLResult } from '@aws-amplify/api';

const client = generateClient();

// Inline GraphQL (like errorLogService — no dependency on codegen output). This lets us select the belongsTo `processedModelSchema { id name }` for labelling/linking. The acknowledge/triage fields (acknowledged/acknowledgedBy/acknowledgedAt) are part of the deployed schema; selection sets below include them so admins can clear FAILED runs from the triage badge. See PROCESSED_MODEL_RUN_TRIAGE.md.

// List run history, newest-first by createdAt. Selects the schema name so rows can label + link it.
const LIST_RUN_HISTORIES = `
  query ListProcessedModelRunHistories(
    $filter: ModelProcessedModelRunHistoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProcessedModelRunHistories(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        processedModelSchemaId
        processedModelSchema { id name }
        modelSchemaTemplateId
        processedModelScheduleId
        status
        description
        startedAt
        completedAt
        executionTimeMs
        outputS3Keys
        acknowledged
        acknowledgedBy
        acknowledgedAt
        owner
        organizationId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Query a single schema's runs via the byProcessedModelSchema GSI (sorted by createdAt DESC).
const RUN_HISTORIES_BY_SCHEMA = `
  query RunHistoriesBySchema(
    $processedModelSchemaId: ID!
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelProcessedModelRunHistoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt(
      processedModelSchemaId: $processedModelSchemaId
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        processedModelSchemaId
        processedModelSchema { id name }
        modelSchemaTemplateId
        processedModelScheduleId
        status
        description
        startedAt
        completedAt
        executionTimeMs
        outputS3Keys
        acknowledged
        acknowledgedBy
        acknowledgedAt
        owner
        organizationId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Lightweight id-only query for counting the FAILED-runs badge (mirrors LIST_ERROR_LOG_IDS).
const LIST_RUN_HISTORY_IDS = `
  query ListRunHistoryIds(
    $filter: ModelProcessedModelRunHistoryFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProcessedModelRunHistories(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { id }
      nextToken
    }
  }
`;

// Mark a FAILED run as triaged. Admin-only (enforced by the existing @auth group rule); this clears the run from the unacknowledged-failed badge for all admins via the onUpdate subscription.
const ACKNOWLEDGE_RUN = `
  mutation AcknowledgeRun($input: UpdateProcessedModelRunHistoryInput!) {
    updateProcessedModelRunHistory(input: $input) {
      id
      status
      acknowledged
      acknowledgedBy
      acknowledgedAt
      updatedAt
    }
  }
`;

// Lightweight schema list for the admin "Processed Models" page — avoids pulling heavy schemaFields.
const LIST_PROCESSED_MODEL_SCHEMAS = `
  query ListProcessedModelSchemasLite(
    $filter: ModelProcessedModelSchemaFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProcessedModelSchemas(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        type
        schemaType
        version
        isActive
        owner
        organizationId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const GET_PROCESSED_MODEL_SCHEMA_META = `
  query GetProcessedModelSchemaMeta($id: ID!) {
    getProcessedModelSchema(id: $id) {
      id
      name
      type
      schemaType
      version
      isActive
      owner
      organizationId
      createdAt
    }
  }
`;

// Fuller schema + its output params, for the expandable row on the admin schemas page.
const GET_PROCESSED_MODEL_SCHEMA_DETAILS = `
  query GetProcessedModelSchemaDetails($id: ID!) {
    getProcessedModelSchema(id: $id) {
      id
      name
      description
      type
      schemaType
      version
      executionUrl
      isActive
      activeStatus
      s3BasePath
      dependency
      runSchedule
      owner
      organizationId
      createdAt
      updatedAt
      outputs {
        items {
          id
          name
          unit
          description
          channels
          outputLocation
          active
          parameterTypeId
        }
      }
    }
  }
`;

// A run's inputs snapshot (AWSJSON, see utils/runInputs.ts) — fetched on demand, never in list queries.
const GET_RUN_INPUTS = `
  query GetRunInputs($id: ID!) {
    getProcessedModelRunHistory(id: $id) {
      id
      processedModelSchemaId
      processedModelSchema { id name }
      modelSchemaTemplateId
      status
      startedAt
      completedAt
      inputs
    }
  }
`;

// The ForecastRecords a run produced, via ForecastRecord's byRun GSI (newest issue first).
const FORECASTS_BY_RUN = `
  query ForecastsByRun($runId: ID!, $limit: Int, $nextToken: String) {
    forecastRecordsByRunIdAndForecastIssued(
      runId: $runId
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        parameterId
        parameter { id name unit }
        forecastIssued
        s3Key
      }
      nextToken
    }
  }
`;

// Which run produced a forecast (null runId = manual upload / backfill). Inputs are fetched separately.
const GET_FORECAST_RECORD_RUN = `
  query GetForecastRecordRun($id: ID!) {
    getForecastRecord(id: $id) {
      id
      runId
      run { id status startedAt completedAt modelSchemaTemplateId }
      uploadId
      uploadedBy
      source
    }
  }
`;

// The ForecastRecord's own copy of its run's inputs.
const GET_FORECAST_RECORD_INPUTS = `
  query GetForecastRecordInputs($id: ID!) {
    getForecastRecord(id: $id) {
      id
      runId
      inputs
    }
  }
`;

const GET_TEMPLATE_FIELD_DEFINITIONS = `
  query GetTemplateFieldDefinitions($id: ID!) {
    getModelSchemaTemplate(id: $id) {
      id
      fieldDefinitions
    }
  }
`;

// Types
export type ProcessedModelRunStatus = 'PROCESSING' | 'COMPLETE' | 'FAILED';

export interface ProcessedModelRunHistory {
  id: string;
  processedModelSchemaId: string;
  processedModelSchema?: { id: string; name?: string | null } | null;
  modelSchemaTemplateId?: string | null;
  processedModelScheduleId?: string | null;
  status: ProcessedModelRunStatus;
  description?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  executionTimeMs?: number | null;
  outputS3Keys?: string | null; // AWSJSON: JSON-encoded array of S3 keys the run wrote (see utils/runHistory.ts)
  acknowledged?: boolean | null;
  acknowledgedBy?: string | null;
  acknowledgedAt?: string | null;
  owner?: string | null;
  organizationId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RunInputsRecord {
  id: string;
  processedModelSchemaId: string;
  processedModelSchema?: { id: string; name?: string | null } | null;
  modelSchemaTemplateId?: string | null;
  status: ProcessedModelRunStatus;
  startedAt?: string | null;
  completedAt?: string | null;
  inputs?: string | null; // AWSJSON snapshot (see utils/runInputs.ts)
}

export interface RunForecast {
  id: string;
  parameterId: string;
  parameter?: { id: string; name?: string | null; unit?: string | null } | null;
  forecastIssued: string;
  s3Key: string;
}

export interface ForecastRecordRun {
  id: string;
  runId?: string | null;
  run?: {
    id: string;
    status: ProcessedModelRunStatus;
    startedAt?: string | null;
    completedAt?: string | null;
    modelSchemaTemplateId?: string | null;
  } | null;
  // Set instead of runId when the record was uploaded into an external forecast model (docs/architecture/EXTERNAL_FORECASTS.md)
  uploadId?: string | null;
  uploadedBy?: string | null;
  source?: "API" | "WEB" | null;
}

export interface ProcessedModelSchemaLite {
  id: string;
  name: string;
  type?: string | null;
  schemaType?: string | null;
  version?: string | null;
  isActive?: boolean | null;
  owner?: string | null;
  organizationId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ProcessedModelOutputParamLite {
  id: string;
  name: string;
  unit?: string | null;
  description?: string | null;
  channels?: string[] | null;
  outputLocation?: string | null;
  active?: boolean | null;
  parameterTypeId?: string | null;
}

export interface ProcessedModelSchemaDetails extends ProcessedModelSchemaLite {
  description?: string | null;
  executionUrl?: string | null;
  activeStatus?: string | null;
  s3BasePath?: string | null;
  dependency?: string | null;
  runSchedule?: string | null; // AWSJSON (string)
  schemaFields?: string | null; // AWSJSON (string)
  outputs?: ProcessedModelOutputParamLite[];
}

export const byCreatedAtDesc = (
  a: ProcessedModelRunHistory,
  b: ProcessedModelRunHistory
) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

export class ProcessedModelRunHistoryService {
  // List run history across all schemas (Admin/AdminReadOnly via auth rules). Optionally filter to FAILED only. Paginates so the filtered set is complete even though DynamoDB applies the filter after the scan limit.
  static async listRuns(opts?: {
    failedOnly?: boolean;
    limit?: number;
    // ProcessedModelRunHistory grants the Admin group a *global* read, so an admin-facing caller must scope it — see getAdminOwnerFilter
    ownerFilter?: object;
    // Lets a caller tell "no runs" apart from "query failed"; default swallows, as getLatestRun does
    throwOnError?: boolean;
    // Called when the 2000-row ceiling stops paging with more left, so a caller can flag a partial list instead of passing it off as complete
    onTruncated?: () => void;
  }): Promise<ProcessedModelRunHistory[]> {
    const clauses = [
      opts?.failedOnly ? { status: { eq: 'FAILED' } } : null,
      opts?.ownerFilter ?? null,
    ].filter(Boolean);
    // `and` rather than a spread: the owner filter is itself an `or`, which a sibling key would overwrite
    const filter =
      clauses.length === 0 ? undefined : clauses.length === 1 ? clauses[0]! : { and: clauses };
    try {
      const items: ProcessedModelRunHistory[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result = (await client.graphql({
          query: LIST_RUN_HISTORIES,
          variables: { filter, limit: opts?.limit ?? 500, nextToken },
        })) as GraphQLResult<{
          listProcessedModelRunHistories: {
            items: ProcessedModelRunHistory[];
            nextToken?: string;
          };
        }>;
        const page = result.data?.listProcessedModelRunHistories;
        items.push(...(page?.items ?? []));
        nextToken = page?.nextToken || undefined;
      } while (nextToken && items.length < 2000);
      if (nextToken) opts?.onTruncated?.();
      return items.sort(byCreatedAtDesc);
    } catch (error) {
      console.error('Error listing run histories:', error);
      if (opts?.throwOnError) throw error;
      return [];
    }
  }

  // Fetch the most recent run for a schema (or null if it has never run). Uses the GSI sorted DESC by createdAt with limit 1 — cheap enough to call per-row on the schemas list page. throwOnError lets a caller tell "never ran" apart from "query failed"; default swallows.
  static async getLatestRun(
    processedModelSchemaId: string,
    { throwOnError = false }: { throwOnError?: boolean } = {}
  ): Promise<ProcessedModelRunHistory | null> {
    const runs = await this.listRunsBySchema(processedModelSchemaId, 1, { throwOnError });
    return runs[0] ?? null;
  }

  // List a single schema's runs (newest first).
  static async listRunsBySchema(
    processedModelSchemaId: string,
    limit: number = 500,
    { throwOnError = false }: { throwOnError?: boolean } = {}
  ): Promise<ProcessedModelRunHistory[]> {
    try {
      const result = (await client.graphql({
        query: RUN_HISTORIES_BY_SCHEMA,
        variables: { processedModelSchemaId, sortDirection: 'DESC', limit },
      })) as GraphQLResult<{
        processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt: {
          items: ProcessedModelRunHistory[];
          nextToken?: string;
        };
      }>;
      return (
        result.data
          ?.processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt
          ?.items ?? []
      );
    } catch (error) {
      console.error('Error listing run histories by schema:', error);
      if (throwOnError) throw error;
      return [];
    }
  }

  // Every run of a schema created at/after `sinceIso` (GSI sort-key condition, newest first,
  // paginated). Feeds the delivery calendar, which needs the whole window rather than the latest N.
  static async listRunsBySchemaSince(
    processedModelSchemaId: string,
    sinceIso: string,
    {
      maxPages = 10,
      onTruncated,
    }: {
      maxPages?: number;
      onTruncated?: () => void;
    } = {}
  ): Promise<ProcessedModelRunHistory[]> {
    const items: ProcessedModelRunHistory[] = [];
    let nextToken: string | undefined;
    let pages = 0;
    do {
      const result = (await client.graphql({
        query: RUN_HISTORIES_BY_SCHEMA,
        variables: {
          processedModelSchemaId,
          createdAt: { ge: sinceIso },
          sortDirection: 'DESC',
          limit: 500,
          nextToken,
        },
      })) as GraphQLResult<{
        processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt: {
          items: ProcessedModelRunHistory[];
          nextToken?: string | null;
        };
      }>;
      const page = result.data?.processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt;
      items.push(...(page?.items ?? []));
      nextToken = page?.nextToken ?? undefined;
      pages += 1;
    } while (nextToken && pages < maxPages);
    if (nextToken) onTruncated?.();
    return items;
  }

  // Count UNACKNOWLEDGED FAILED runs (drives the blue admin-nav badge). Paginated. Filters to FAILED runs that are either not yet acknowledged or are legacy rows written before the acknowledged field existed (attributeExists:false). Acknowledging a run clears it from this count. The badge only renders "99+" past 99, so we cap the scan at a fixed number of pages to bound the work as failures accumulate (the exact count beyond the cap is irrelevant to the UI).
  static async countUnacknowledgedFailed(): Promise<number> {
    const MAX_PAGES = 10; // 10 x 1000 ids — far beyond the "99+" display threshold
    const filter = {
      and: [
        { status: { eq: 'FAILED' } },
        {
          or: [
            { acknowledged: { eq: false } },
            { acknowledged: { attributeExists: false } }, // legacy rows written before the field existed
          ],
        },
      ],
    };
    try {
      let count = 0;
      let nextToken: string | undefined = undefined;
      let pages = 0;
      do {
        const result = (await client.graphql({
          query: LIST_RUN_HISTORY_IDS,
          variables: { filter, limit: 1000, nextToken },
        })) as GraphQLResult<{
          listProcessedModelRunHistories: {
            items: { id: string }[];
            nextToken?: string;
          };
        }>;
        const page = result.data?.listProcessedModelRunHistories;
        count += page?.items?.length || 0;
        nextToken = page?.nextToken || undefined;
        pages += 1;
      } while (nextToken && pages < MAX_PAGES);
      return count;
    } catch (error) {
      console.error('Error counting unacknowledged failed runs:', error);
      return 0;
    }
  }

  // Mark a FAILED run as triaged (Admin only). Stamps acknowledgedBy/acknowledgedAt so the row shows who handled it and the unacknowledged-failed badge decrements for all admins.
  static async acknowledgeRun(
    id: string,
    acknowledgedBy: string
  ): Promise<ProcessedModelRunHistory | null> {
    try {
      const result = (await client.graphql({
        query: ACKNOWLEDGE_RUN,
        variables: {
          input: {
            id,
            acknowledged: true,
            acknowledgedBy,
            acknowledgedAt: new Date().toISOString(),
          },
        },
      })) as GraphQLResult<{
        updateProcessedModelRunHistory: ProcessedModelRunHistory;
      }>;
      return result.data?.updateProcessedModelRunHistory ?? null;
    } catch (error) {
      console.error('Error acknowledging run:', error);
      return null;
    }
  }

  // List all ProcessedModelSchemas (lightweight) for the admin schemas page.
  static async listSchemas(
    limit: number = 500,
    opts?: { filter?: object; throwOnError?: boolean; onTruncated?: () => void }
  ): Promise<ProcessedModelSchemaLite[]> {
    try {
      const items: ProcessedModelSchemaLite[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result = (await client.graphql({
          query: LIST_PROCESSED_MODEL_SCHEMAS,
          variables: { filter: opts?.filter, limit, nextToken },
        })) as GraphQLResult<{
          listProcessedModelSchemas: {
            items: ProcessedModelSchemaLite[];
            nextToken?: string;
          };
        }>;
        const page = result.data?.listProcessedModelSchemas;
        items.push(...(page?.items ?? []));
        nextToken = page?.nextToken || undefined;
      } while (nextToken && items.length < 2000);
      if (nextToken) opts?.onTruncated?.();
      return items;
    } catch (error) {
      console.error('Error listing processed model schemas:', error);
      if (opts?.throwOnError) throw error;
      return [];
    }
  }

  // Fetch a single schema's metadata (for the per-schema page header).
  static async getSchemaMeta(
    id: string
  ): Promise<ProcessedModelSchemaLite | null> {
    try {
      const result = (await client.graphql({
        query: GET_PROCESSED_MODEL_SCHEMA_META,
        variables: { id },
      })) as GraphQLResult<{ getProcessedModelSchema: ProcessedModelSchemaLite }>;
      return result.data?.getProcessedModelSchema ?? null;
    } catch (error) {
      console.error('Error fetching schema meta:', error);
      return null;
    }
  }

  // Fetch a single schema's full details + its output params (for the expandable admin row).
  static async getSchemaDetails(
    id: string
  ): Promise<ProcessedModelSchemaDetails | null> {
    try {
      const result = (await client.graphql({
        query: GET_PROCESSED_MODEL_SCHEMA_DETAILS,
        variables: { id },
      })) as GraphQLResult<{
        getProcessedModelSchema:
          | (ProcessedModelSchemaDetails & {
              outputs?: { items?: ProcessedModelOutputParamLite[] };
            })
          | null;
      }>;
      const raw = result.data?.getProcessedModelSchema;
      if (!raw) return null;
      return {
        ...raw,
        outputs: raw.outputs?.items ?? [],
      };
    } catch (error) {
      console.error('Error fetching schema details:', error);
      return null;
    }
  }

  // A run plus its inputs snapshot, for the run inputs modal. throwOnError lets the caller tell "no run"
  // apart from "query failed" (as getLatestRun does); default swallows.
  static async getRunInputs(
    id: string,
    { throwOnError = false }: { throwOnError?: boolean } = {}
  ): Promise<RunInputsRecord | null> {
    try {
      const result = (await client.graphql({
        query: GET_RUN_INPUTS,
        variables: { id },
      })) as GraphQLResult<{ getProcessedModelRunHistory: RunInputsRecord | null }>;
      return result.data?.getProcessedModelRunHistory ?? null;
    } catch (error) {
      console.error('Error fetching run inputs:', error);
      if (throwOnError) throw error;
      return null;
    }
  }

  // Every ForecastRecord a run produced (byRun GSI). Runs from before the link existed return [].
  static async listForecastsByRun(
    runId: string,
    { throwOnError = false }: { throwOnError?: boolean } = {}
  ): Promise<RunForecast[]> {
    try {
      const items: RunForecast[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result = (await client.graphql({
          query: FORECASTS_BY_RUN,
          variables: { runId, limit: 100, nextToken },
        })) as GraphQLResult<{
          forecastRecordsByRunIdAndForecastIssued: { items: RunForecast[]; nextToken?: string };
        }>;
        const page = result.data?.forecastRecordsByRunIdAndForecastIssued;
        items.push(...(page?.items ?? []));
        nextToken = page?.nextToken || undefined;
      } while (nextToken && items.length < 500);
      return items;
    } catch (error) {
      console.error('Error listing forecasts by run:', error);
      if (throwOnError) throw error;
      return [];
    }
  }

  // The run that produced a forecast, or a record with runId null for manual uploads.
  static async getForecastRecordRun(
    id: string,
    { throwOnError = false }: { throwOnError?: boolean } = {}
  ): Promise<ForecastRecordRun | null> {
    try {
      const result = (await client.graphql({
        query: GET_FORECAST_RECORD_RUN,
        variables: { id },
      })) as GraphQLResult<{ getForecastRecord: ForecastRecordRun | null }>;
      return result.data?.getForecastRecord ?? null;
    } catch (error) {
      console.error('Error fetching forecast record run:', error);
      if (throwOnError) throw error;
      return null;
    }
  }

  // A ForecastRecord's copy of its run's inputs (AWSJSON string, or null).
  static async getForecastRecordInputs(
    id: string,
    { throwOnError = false }: { throwOnError?: boolean } = {}
  ): Promise<string | null> {
    try {
      const result = (await client.graphql({
        query: GET_FORECAST_RECORD_INPUTS,
        variables: { id },
      })) as GraphQLResult<{ getForecastRecord: { inputs?: string | null } | null }>;
      return result.data?.getForecastRecord?.inputs ?? null;
    } catch (error) {
      console.error('Error fetching forecast record inputs:', error);
      if (throwOnError) throw error;
      return null;
    }
  }

  // A template's fieldDefinitions (AWSJSON string), used to label input fields.
  static async getTemplateFieldDefinitions(id: string): Promise<string | null> {
    try {
      const result = (await client.graphql({
        query: GET_TEMPLATE_FIELD_DEFINITIONS,
        variables: { id },
      })) as GraphQLResult<{ getModelSchemaTemplate: { fieldDefinitions?: string | null } | null }>;
      return result.data?.getModelSchemaTemplate?.fieldDefinitions ?? null;
    } catch (error) {
      console.error('Error fetching template field definitions:', error);
      return null;
    }
  }
}
